import React, { createContext } from 'react';

import { SelectField } from '@components/CitySelect';
import { DELIVERY_DEFAULT_CITY } from '@consts/deliveryConsts';
import type { CityType, DeliveryInfoArgs } from '@customTypes/delivery';
import { useCities, useDeliveryInfoByProduct } from '@hooks/delivery-hooks';
import gtm from '@periodica/analytics/gtm';
import { DELIVERY_TYPE_COURIER, DELIVERY_TYPE_PICKUP } from '@periodica/consts';
import { Heading, Loader, Paragraph, Separator, SupportLink } from '@periodica/ui-kit';
import { Svg } from '@periodica/ui-kit/icons';
import { humanizeDate } from '@utils/humanizeDate';
import { currency } from '@utils/numbers';
import cn from 'classnames';

import * as styles from './styles.module.css';

interface DeliveryWidgetArgs {
  productType: string;
  productSize: string;
}

const selectedCityContext = createContext<
  [CityType, React.Dispatch<React.SetStateAction<CityType>>] | null
>(null);

export function SelectedCityProvider({ children }: { children: React.ReactNode }) {
  const state = React.useState<CityType>(DELIVERY_DEFAULT_CITY);
  return <selectedCityContext.Provider value={state}>{children}</selectedCityContext.Provider>;
}

function useSelectedCity() {
  const context = React.useContext(selectedCityContext);
  if (!context) {
    throw new Error('useSelectedCity must be used within a SelectedCityProvider');
  }
  return context;
}

export function DeliveryWidget({ productType, productSize }: DeliveryWidgetArgs) {
  const [selectedCity, setSelectedCity] = useSelectedCity();
  const { data: cities, isLoading, isError } = useCities();

  const handleChangeCity = (selectedCityId: string) => {
    if (cities) {
      gtm.logEvent({ name: 'event_site_wgt_pick_city' });
      setSelectedCity(cities.entities[selectedCityId]);
    }
  };

  if (isError) {
    return (
      <div className={styles.wrapper}>
        <ErrorContent />
      </div>
    );
  }

  if (isLoading || cities === undefined) {
    return (
      <div className={cn(styles.wrapper)}>
        <Heading variant="h3" customTag="h2" color="primary">
          Способы и сроки получения
        </Heading>

        <div className={styles.loaderWrapper}>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.wrapper)}>
      <Heading variant="h3" customTag="h2" color="primary">
        Способы и сроки получения
      </Heading>

      <SelectField
        value={`${selectedCity.id}`}
        onChange={handleChangeCity}
        cities={cities.entities}
      />

      <ProvidersWithInfo product={{ type: productType, size: productSize }} city={selectedCity} />
    </div>
  );
}

type ProvidersWithInfoProps = {
  product: { type: string; size: string };
  city: CityType;
};

type DeliveryType = typeof DELIVERY_TYPE_PICKUP | typeof DELIVERY_TYPE_COURIER;

export function getSupportedDeliveryTypes(city: CityType) {
  const supportedTypes: DeliveryType[] = [];
  if (city.pickup) {
    supportedTypes.push(DELIVERY_TYPE_PICKUP);
  }
  if (city.courier) {
    supportedTypes.push(DELIVERY_TYPE_COURIER);
  }

  return supportedTypes;
}

function ProvidersWithInfo(props: ProvidersWithInfoProps) {
  const { product, city } = props;
  const deliveryTypes = getSupportedDeliveryTypes(city);
  const { data, isLoading, isError } = useDeliveryInfoByProduct({
    product,
    cityId: city.id,
    deliveryTypes,
  });

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper} style={{ height: 153 }}>
        <Loader />
      </div>
    );
  }

  if (
    isError ||
    deliveryTypes.every((deliveryType) => !data[deliveryType] || data[deliveryType]?.length === 0)
  ) {
    return <ErrorContent />;
  }

  return (
    <>
      {Object.entries(data)
        .filter(([_, deliveryItems]) => !!deliveryItems && deliveryItems.length > 0)
        .map(([deliveryType, deliveryItems], index) => (
          <React.Fragment key={deliveryType}>
            {index > 0 && <Separator />}
            <DeliveryTypeInfo
              deliveryItems={deliveryItems}
              deliveryType={deliveryType as DeliveryType}
            />
          </React.Fragment>
        ))}
      <Paragraph size="small" color="secondary">
        Срок доставки с учетом изготовления, если заказать сегодня.
      </Paragraph>
    </>
  );
}

const humanizeDeliveryTitles = (provieder: string, deliveryType: string) => {
  if (deliveryType === DELIVERY_TYPE_COURIER) {
    switch (provieder) {
      case 'CDEK':
        return 'Курьером CDEK';
      case 'FIVE_POST':
        return 'Курьером 5Post';
      case 'YANDEX':
        return 'Курьером Яндекс.Доставка';
      default:
        return `Курьером ${provieder}`;
    }
  } else {
    switch (provieder) {
      case 'CDEK':
        return 'CDEK';
      case 'FIVE_POST':
        return '5Post';
      case 'YANDEX':
        return 'Яндекс.Доставка';
      default:
        return provieder;
    }
  }
};

type DeliveryTypeInfoProps = {
  deliveryItems: DeliveryInfoArgs[];
  deliveryType: typeof DELIVERY_TYPE_COURIER | typeof DELIVERY_TYPE_PICKUP;
};

function DeliveryTypeInfo({ deliveryItems, deliveryType }: DeliveryTypeInfoProps) {
  return (
    <div className={styles.deliveryWrapper}>
      {deliveryType === DELIVERY_TYPE_COURIER ? <CourierDelivery /> : <PickpointDelivery />}
      <div className={styles.deliveryContent}>
        {deliveryItems.map((item) => (
          <div className={styles.deliveryProvider} key={item.delivery_provider}>
            <div className={styles.deliveryInfo}>
              <Paragraph size="large" color="primary">
                {humanizeDeliveryTitles(item.delivery_provider, deliveryType)}
              </Paragraph>
              <Paragraph size="large" color="primary" weight="emphasized">
                {humanizeDate(item.raw_date!)}
              </Paragraph>
            </div>
            <Paragraph size="large" color="secondary">
              {currency(item.sum)}&nbsp;₽
            </Paragraph>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorContent() {
  return (
    <Paragraph color="primary">
      Не удалось загрузить данные для этого продукта. Попробуйте обновить страницу или{' '}
      <SupportLink>обратитесь в поддержку</SupportLink>
    </Paragraph>
  );
}

function PickpointDelivery() {
  return (
    <Svg width={24} height={24} data-icon="PickpointDelivery">
      <path
        fill="currentColor"
        d="m20.97 6.202-8.25-4.514a1.49 1.49 0 0 0-1.44 0L3.03 6.204a1.5 1.5 0 0 0-.78 1.312v8.966a1.5 1.5 0 0 0 .78 1.313l8.25 4.516a1.49 1.49 0 0 0 1.44 0l8.25-4.516a1.5 1.5 0 0 0 .78-1.313V7.517a1.5 1.5 0 0 0-.78-1.315M12 3l7.532 4.125-2.791 1.528-7.533-4.125zm0 8.25L4.468 7.125l3.178-1.74 7.532 4.125zM3.75 8.438l7.5 4.104v8.043l-7.5-4.102zm16.5 8.042-7.5 4.105v-8.04l3-1.64v3.345a.75.75 0 1 0 1.5 0v-4.167l3-1.645z"
      />
    </Svg>
  );
}
function CourierDelivery() {
  return (
    <Svg width={24} height={24} data-icon="CourierDelivery">
      <path
        fill="currentColor"
        d="M15.375 7.5a2.625 2.625 0 1 0 0-5.25 2.625 2.625 0 0 0 0 5.25m0-3.75a1.125 1.125 0 1 1 0 2.25 1.125 1.125 0 0 1 0-2.25m3.375 9a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5m0 6a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5m-13.5-6a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5m0 6a2.25 2.25 0 1 1 0-4.5 2.25 2.25 0 0 1 0 4.5M18 11.25h-3.75a.75.75 0 0 1-.53-.22l-2.47-2.47-1.94 1.94 3.22 3.22a.75.75 0 0 1 .22.53v4.5a.75.75 0 1 1-1.5 0v-4.19l-3.53-3.53a.75.75 0 0 1 0-1.06l3-3a.75.75 0 0 1 1.06 0l2.78 2.78H18a.75.75 0 1 1 0 1.5"
      />
    </Svg>
  );
}
